import React, { useState } from 'react';
import { Link, useLocation } from 'wouter';
import { Menu, X, Search, ShoppingBag, Heart, User, LogIn, ChevronRight, Phone } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import CartDrawer from './CartDrawer';
import { motion, AnimatePresence } from 'motion/react';

interface StoreHeaderProps {
  onSearchChange?: (query: string) => void;
  searchQuery?: string;
  showSearchBarDirectly?: boolean;
}

export default function StoreHeader({
  onSearchChange,
  searchQuery = '',
  showSearchBarDirectly = false
}: StoreHeaderProps) {
  const [isOpenMenu, setIsOpenMenu] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(showSearchBarDirectly);
  const [localSearch, setLocalSearch] = useState(searchQuery);
  const [location, setLocation] = useLocation();
  const { totalItems } = useCart();
  const { user, logout } = useAuth();

  const handleSearchInput = (val: string) => {
    setLocalSearch(val);
    if (onSearchChange) {
      onSearchChange(val);
    }
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'All Spices', path: '/products' },
    { name: 'Powders', path: '/products?filter=powder' },
    { name: 'Blended Masalas', path: '/products?filter=masala' },
    { name: 'Whole Spices', path: '/products?filter=whole' },
    { name: 'Lookbook / Brochure', path: '/brochure' },
    { name: 'Wholesale & B2B', path: '/wholesale' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact Us', path: '/contact' },
  ];

  return (
    <>
      <header className="sticky top-0 z-40 w-full bg-[#FAF7F2] border-b border-[#E5E0D8] shadow-sm transition-all duration-300">
        <div className="max-w-7xl mx-auto px-3 sm:px-6">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Left Icons: Circular Hamburger Menu & Search */}
            <div className="flex items-center gap-1.5 sm:gap-2.5">
              {/* Circular light grey hamburger menu button matching Screenshot 1 */}
              <button
                type="button"
                onClick={() => setIsOpenMenu(true)}
                aria-label="Open menu"
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#EAE6DF] hover:bg-[#DDD8D0] flex items-center justify-center text-[#222222] transition-colors active:scale-95"
              >
                <Menu className="w-5 h-5 stroke-[2]" />
              </button>

              {/* Search Icon */}
              <button
                type="button"
                onClick={() => {
                  if (location !== '/' && location !== '/products') {
                    setLocation('/products');
                  }
                  setIsSearchOpen(!isSearchOpen);
                }}
                aria-label="Search spices"
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-[#222222] hover:text-[#D97736] transition-colors active:scale-95"
              >
                <Search className="w-5 h-5 stroke-[2]" />
              </button>
            </div>

            {/* Center: Brand Logo & Slogan matching Screenshot 1 */}
            <div className="flex-1 flex flex-col items-center justify-center text-center px-2">
              <Link href="/" className="inline-block group cursor-pointer">
                <span className="font-serif font-extrabold text-lg sm:text-2xl tracking-tight text-[#1F1F1F] group-hover:text-[#D97736] transition-colors uppercase">
                  Ajjas <span className="text-[#C85A17]">Masala</span>
                </span>
                <span className="block text-[7px] sm:text-[9px] font-bold uppercase tracking-[0.22em] text-[#C85A17] -mt-0.5 sm:-mt-1">
                  The Joy of Good Taste
                </span>
              </Link>
            </div>

            {/* Right: Shopping Bag Icon matching Screenshot 1 */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIsCartOpen(true)}
                aria-label="View shopping bag"
                className="relative w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center text-[#222222] hover:text-[#D97736] transition-colors active:scale-95 cursor-pointer"
              >
                <ShoppingBag className="w-5 h-5 stroke-[1.8]" />
                {totalItems > 0 && (
                  <span className="absolute top-0 right-0 bg-[#D97736] text-white text-[9px] font-bold min-w-[17px] h-[17px] rounded-full flex items-center justify-center ring-2 ring-[#FAF7F2] shadow-sm">
                    {totalItems}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Expandable Search Bar */}
          <AnimatePresence>
            {isSearchOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden pb-3 px-1"
              >
                <div className="relative flex items-center bg-white rounded-lg border border-[#D5D0C8] shadow-inner">
                  <Search className="absolute left-3 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={localSearch}
                    onChange={(e) => handleSearchInput(e.target.value)}
                    placeholder="Search powders, masalas, spices..."
                    autoFocus
                    className="w-full py-2.5 pl-9 pr-8 text-xs sm:text-sm bg-transparent outline-none text-[#1F1F1F] placeholder:text-gray-400 font-medium"
                  />
                  {localSearch && (
                    <button
                      type="button"
                      onClick={() => handleSearchInput('')}
                      className="absolute right-2.5 text-gray-400 hover:text-black"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </header>

      {/* Slide-out Navigation Drawer */}
      <AnimatePresence>
        {isOpenMenu && (
          <div className="fixed inset-0 z-50 flex">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpenMenu(false)}
              className="fixed inset-0 bg-black/50 backdrop-blur-[2px] cursor-pointer"
            />

            {/* Side Drawer */}
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 220 }}
              className="relative w-full max-w-[300px] bg-[#FAF7F2] h-full z-50 flex flex-col shadow-2xl border-r border-[#E5E0D8]"
            >
              {/* Drawer Header */}
              <div className="p-4 border-b border-[#E5E0D8] flex items-center justify-between bg-white">
                <div>
                  <h3 className="font-serif font-bold text-lg text-[#1F1F1F] uppercase">Ajjas Masala</h3>
                  <p className="text-[8px] font-bold uppercase tracking-[0.2em] text-[#C85A17]">The Joy of Good Taste</p>
                </div>
                <button
                  onClick={() => setIsOpenMenu(false)}
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-500 hover:text-black transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Navigation Links */}
              <div className="flex-1 overflow-y-auto py-3">
                <div className="px-2 space-y-0.5">
                  {navLinks.map((link) => {
                    const isActive = location === link.path;
                    return (
                      <Link
                        key={link.path}
                        href={link.path}
                        onClick={() => setIsOpenMenu(false)}
                        className={`flex items-center justify-between px-4 py-3 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors ${
                          isActive
                            ? 'bg-[#2B2B2B] text-white'
                            : 'text-[#333333] hover:bg-[#EAE6DF] hover:text-black'
                        }`}
                      >
                        <span>{link.name}</span>
                        <ChevronRight className="w-3.5 h-3.5 opacity-40" />
                      </Link>
                    );
                  })}
                </div>

                <div className="h-[1px] bg-[#E5E0D8] my-3 mx-4" />

                {/* Account / Orders */}
                <div className="px-2 space-y-0.5">
                  <Link
                    href="/orders"
                    onClick={() => setIsOpenMenu(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#333333] hover:bg-[#EAE6DF] rounded-lg transition-colors"
                  >
                    <span>My Orders</span>
                  </Link>
                  <Link
                    href="/wishlist"
                    onClick={() => setIsOpenMenu(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#333333] hover:bg-[#EAE6DF] rounded-lg transition-colors"
                  >
                    <span>Wishlist</span>
                  </Link>
                  <Link
                    href="/account"
                    onClick={() => setIsOpenMenu(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#333333] hover:bg-[#EAE6DF] rounded-lg transition-colors"
                  >
                    <span>My Account</span>
                  </Link>
                </div>
              </div>

              {/* Drawer Footer with Login/Logout */}
              <div className="p-4 border-t border-[#E5E0D8] bg-white">
                {user ? (
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 pr-2">
                      <p className="text-xs font-bold text-[#1F1F1F] truncate">{user.displayName || 'Customer'}</p>
                      <p className="text-[10px] text-gray-500 truncate">{user.email}</p>
                    </div>
                    <button
                      onClick={() => {
                        logout();
                        setIsOpenMenu(false);
                      }}
                      className="text-xs font-bold text-[#D97736] hover:underline"
                    >
                      Logout
                    </button>
                  </div>
                ) : (
                  <Link
                    href="/login"
                    onClick={() => setIsOpenMenu(false)}
                    className="w-full py-2.5 px-4 btn-terracotta bg-[#D97736] text-white rounded font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 shadow-sm"
                  >
                    <LogIn className="w-4 h-4" />
                    <span>Customer Login</span>
                  </Link>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Cart Drawer */}
      <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </>
  );
}
